import { useState, useEffect } from "react";
import { useCustomizationStore } from "features/ManageWebpage/modules/Customizations/store/useCustomizationStore";
import { useWebpageSectionChange } from "features/ManageWebpage/modules/Customizations/store/useWebpageSectionChange";
import { CUSTOMIZATION_SECTIONKEYS } from "features/ManageWebpage/modules/Customizations/common/utils/Customization.constants";
import {
  DEFAULT_COLORS,
  DEFAULT_TEMPLATE_COLORS,
  MAPPED_COLOR_KEYS,
  TEMPLATE_COLOR_KEYS,
  TEMPLATE_COLOR_KEY_PREFIX,
} from "../ColorPalette.constants";

const hexToRgb = (value) => {
  if (!value || typeof value !== "string") return value;
  if (/^#?[a-f\d]{6}$/i.test(value)) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(value);
    return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
      result[3],
      16
    )}`;
  }
  return value;
};

const rgbToHex = (value) => {
  if (!value || typeof value !== "string") return value;
  if (/^\d+\s*,\s*\d+\s*,\s*\d+$/.test(value)) {
    const [r, g, b] = value.split(",").map((p) => parseInt(p.trim()));
    return `#${[r, g, b]
      .map((x) => x.toString(16).padStart(2, "0"))
      .join("")}`.toUpperCase();
  }
  return value;
};

export const useColorPalette = () => {
  const sectionConfig = useCustomizationStore((state) => state.sectionConfig);
  const sectionValue =
    sectionConfig[CUSTOMIZATION_SECTIONKEYS.COLOR_PALETTE]?.value || {};

  const [colors, setColors] = useState({
    "template-primary-color":
      sectionValue["template-primary-color"] ||
      DEFAULT_TEMPLATE_COLORS["template-primary-color"],
    "template-secondary-color":
      sectionValue["template-secondary-color"] ||
      DEFAULT_TEMPLATE_COLORS["template-secondary-color"],
    "template-page-background":
      sectionValue["template-page-background"] ||
      DEFAULT_TEMPLATE_COLORS["template-page-background"],
    "template-primary-button-text-color":
      sectionValue["template-primary-button-text-color"] ||
      DEFAULT_TEMPLATE_COLORS["template-primary-button-text-color"],
  });

  const sanitizedTemplateColors = (source) =>
    TEMPLATE_COLOR_KEYS.reduce(
      (acc, key) => ({
        ...acc,
        [key]: rgbToHex(source[key] || DEFAULT_TEMPLATE_COLORS[key]),
      }),
      {}
    );

  const buildFeatureValue = (source) => {
    const mappedColors = MAPPED_COLOR_KEYS.reduce(
      (acc, key) => ({
        ...acc,
        [key]: sectionValue[key] || DEFAULT_COLORS[key],
      }),
      {}
    );
    const templateColors = Object.entries(sectionValue).reduce(
      (acc, [key, value]) => {
        if (key.startsWith(TEMPLATE_COLOR_KEY_PREFIX)) {
          const updatedValue = source[key] || value;
          acc[key] = hexToRgb(updatedValue);
        }
        return acc;
      },
      {}
    );
    return { ...mappedColors, ...templateColors };
  };

  const { handleSectionChange } = useWebpageSectionChange();

  useEffect(() => {
    if (
      sectionValue &&
      typeof sectionValue === "object" &&
      Object.keys(sectionValue).length > 0
    ) {
      setColors((prev) => ({
        ...prev,
        ...sanitizedTemplateColors(sectionValue),
      }));
    }
  }, [sectionValue]);

  const updateColor = (key, value) => {
    const updatedColors = { ...colors, [key]: value };
    setColors(updatedColors);

    const payload = {
      feature_key: CUSTOMIZATION_SECTIONKEYS.COLOR_PALETTE,
      feature_value: buildFeatureValue(updatedColors),
    };
    handleSectionChange({ payload });
  };

  return {
    colors,
    updateColor,
  };
};
